import React from 'react'
import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'
import { PAGE_TAB_FONT, PageTitle } from 'components/PageTitle'
import { STICKY_FILTER_HEIGHT } from 'core/theme.tokens'

interface AdminPageFrameProps {
  title?: string
  section?: string
  subtitle?: string
  action?: React.ReactNode
  filters?: React.ReactNode
  children: React.ReactNode
}

export const AdminPageFrame: React.FC<AdminPageFrameProps> = ({
  title,
  section,
  subtitle,
  action,
  filters,
  children,
}) => {
  return (
    <Box
      sx={theme => ({
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, md: 1.25 },
        pt: 0,
        '& .MuiTabs-root': {
          minHeight: 34,
          minWidth: 0,
        },
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiTabs-flexContainer': {
          gap: 0.5,
          flexWrap: 'wrap',
        },
        '& .MuiTab-root': {
          textTransform: 'none',
          minHeight: 32,
          minWidth: 0,
          px: 1.25,
          py: 0.5,
          borderRadius: 999,
          fontSize: PAGE_TAB_FONT,
          fontWeight: 600,
          color: 'text.secondary',
          opacity: 1,
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.88),
            color: theme.palette.primary.contrastText,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.92),
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '& .MuiChip-root': {
          height: 20,
          fontSize: '0.68rem',
          fontWeight: 600,
          '& .MuiChip-label': {
            px: 0.625,
          },
          '& .MuiChip-icon': {
            fontSize: 14,
            ml: 0.5,
            mr: -0.25,
          },
        },
        '& .MuiDataGrid-root': {
          '--DataGrid-rowBorderColor': theme.palette.divider,
          fontSize: '0.8125rem',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
          '& .MuiDataGrid-cell': {
            py: 0.25,
          },
        },
      })}
    >
      {title ? (
        <PageTitle title={title} section={section} subtitle={subtitle} action={action} />
      ) : null}
      {filters ? (
        <Box
          sx={{
            position: { xs: 'sticky', md: 'static' },
            top: 0,
            zIndex: 2,
            minHeight: { xs: STICKY_FILTER_HEIGHT, md: 'auto' },
            bgcolor: 'background.default',
            py: { xs: 0.5, md: 0 },
          }}
        >
          {filters}
        </Box>
      ) : null}
      {children}
    </Box>
  )
}
